import { format } from "date-fns";

import type { Deal, DealStage } from "../types";

export const RECURRING_INTERVAL_CHOICES = [
  { id: "monthly", name: "Månadsvis" },
  { id: "quarterly", name: "Kvartalsvis" },
  { id: "yearly", name: "Årsvis" },
];

export function annualizeRecurring(
  amount: number | null | undefined,
  interval: string | null | undefined,
): number {
  if (!amount || !interval) return 0;
  switch (interval) {
    case "monthly":
      return amount * 12;
    case "quarterly":
      return amount * 4;
    case "yearly":
      return amount;
    default:
      return 0;
  }
}

export function totalDealValue(
  deal: Pick<Deal, "amount" | "recurring_amount" | "recurring_interval">,
): number {
  return (
    (deal.amount || 0) +
    annualizeRecurring(deal.recurring_amount, deal.recurring_interval)
  );
}

export function formatRecurringLabel(
  interval: string | null | undefined,
): string {
  switch (interval) {
    case "monthly":
      return "/mån";
    case "quarterly":
      return "/kv";
    case "yearly":
      return "/år";
    default:
      return "";
  }
}

export const findDealLabel = (dealStages: DealStage[], dealValue: string) => {
  const dealStage = dealStages.find((stage) => stage.value === dealValue);
  return dealStage?.label;
};

export function getRelativeTimeString(
  dateString: string,
  locale = "en",
): string {
  const date = new Date(dateString);
  date.setHours(0, 0, 0, 0);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const diff = date.getTime() - today.getTime();
  const unitDiff = Math.round(diff / (1000 * 60 * 60 * 24));

  // Check if the date is more than one week old
  if (Math.abs(unitDiff) > 7) {
    return new Intl.DateTimeFormat(locale, {
      day: "numeric",
      month: "long",
    }).format(date);
  }

  // Intl.RelativeTimeFormat for dates within the last week
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
  return ucFirst(rtf.format(unitDiff, "day"));
}

function ucFirst(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

const isoDateStringRegex = /^\d{4}-\d{2}-\d{2}$/;

export function formatISODateString(dateString: string) {
  if (!isoDateStringRegex.test(dateString)) {
    throw new Error("Invalid date format. Expected YYYY-MM-DD.");
  }
  // Some browsers will consider a date in the format YYYY-MM-DD as UTC, which can cause off-by-one-day issues depending on the user's timezone.
  // To avoid this, we can parse the date components manually and create a date object in the local timezone.
  const [year, month, day] = dateString.split("-").map(Number);
  const date = new Date(year, month - 1, day);

  return format(date, "PP");
}
